import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Plus, Trash2, GripVertical } from "lucide-react";

export interface Exercise {
  id: string;
  name: string;
  sets: string;
  reps: string;
  weight: string;
  notes: string;
}

interface ExerciseLogInputProps {
  exercises: Exercise[];
  onChange: (exercises: Exercise[]) => void;
  readOnly?: boolean;
}

const ExerciseLogInput = ({
  exercises,
  onChange,
  readOnly = false,
}: ExerciseLogInputProps) => {
  const addExercise = () => {
    onChange([
      ...exercises,
      {
        id: crypto.randomUUID(),
        name: "",
        sets: "",
        reps: "",
        weight: "",
        notes: "",
      },
    ]);
  };

  const updateExercise = (id: string, field: keyof Exercise, value: string) => {
    onChange(
      exercises.map((ex) => (ex.id === id ? { ...ex, [field]: value } : ex))
    );
  };

  const removeExercise = (id: string) => {
    onChange(exercises.filter((ex) => ex.id !== id));
  };

  if (readOnly) {
    if (exercises.length === 0) {
      return (
        <div className="text-sm text-muted-foreground">
          No exercises recorded
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {exercises.map((exercise, index) => (
          <div
            key={exercise.id}
            className="flex items-start gap-2 text-sm py-2 border-b last:border-0"
          >
            <span className="text-muted-foreground w-6">{index + 1}.</span>
            <div className="flex-1">
              <span className="font-medium">{exercise.name}</span>
              <span className="text-muted-foreground ml-2">
                {exercise.sets && exercise.reps && (
                  <>
                    {exercise.sets}x{exercise.reps}
                  </>
                )}
                {exercise.weight && (
                  <span className="ml-1">@ {exercise.weight}</span>
                )}
              </span>
              {exercise.notes && (
                <p className="text-muted-foreground text-xs mt-1">
                  {exercise.notes}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>Exercises</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addExercise}
          className="gap-1"
        >
          <Plus className="h-4 w-4" />
          Add Exercise
        </Button>
      </div>

      {exercises.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-4 border rounded-md border-dashed">
          No exercises added. Click "Add Exercise" to start.
        </div>
      ) : (
        <div className="space-y-3">
          {exercises.map((exercise, index) => (
            <Card key={exercise.id} className="p-3">
              <div className="flex items-start gap-2">
                <div className="text-muted-foreground pt-2">
                  <GripVertical className="h-4 w-4" />
                </div>
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground w-6">
                      {index + 1}.
                    </span>
                    <Input
                      placeholder="Exercise name"
                      value={exercise.name}
                      onChange={(e) =>
                        updateExercise(exercise.id, "name", e.target.value)
                      }
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeExercise(exercise.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">Sets</Label>
                      <Input
                        placeholder="4"
                        value={exercise.sets}
                        onChange={(e) =>
                          updateExercise(exercise.id, "sets", e.target.value)
                        }
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Reps</Label>
                      <Input
                        placeholder="10"
                        value={exercise.reps}
                        onChange={(e) =>
                          updateExercise(exercise.id, "reps", e.target.value)
                        }
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Weight</Label>
                      <Input
                        placeholder="135 lbs"
                        value={exercise.weight}
                        onChange={(e) =>
                          updateExercise(exercise.id, "weight", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <Input
                    placeholder="Notes (optional)"
                    value={exercise.notes}
                    onChange={(e) =>
                      updateExercise(exercise.id, "notes", e.target.value)
                    }
                    className="text-sm"
                  />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExerciseLogInput;
